import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdLocationOn, MdEdit, MdSave, MdRouter, MdInventory,
  MdAccountTree, MdRefresh, MdInfo, MdSwapHoriz,
} from 'react-icons/md';
import api from '../api/axios';
import SearchableSelect from '../components/SearchableSelect';

const Toast = ({ msg, type, onClose }) => (
  <div style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 18px', borderRadius: 8, color: 'white',
    background: type === 'error' ? '#d32f2f' : type === 'warning' ? '#f57c00' : '#2e7d32',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420,
  }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18 }}>×</button>
  </div>
);

const LocationDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [loc, setLoc] = useState(null);
  const [assets, setAssets] = useState([]);
  const [readers, setReaders] = useState([]);
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    try {
      const [l, a, r, all] = await Promise.all([
        api.get(`/locations/${id}`),
        api.get(`/locations/${id}/assets${includeDescendants ? '?includeDescendants=true' : ''}`),
        api.get(`/locations/${id}/readers`),
        api.get('/locations'),
      ]);
      setLoc(l.data);
      setAssets(a.data);
      setReaders(r.data);
      setAllLocations(all.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load', 'error');
    }
  }, [id, includeDescendants]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = () => { setDraft({ ...loc }); setEditing(true); };
  const handleCancel = () => { setEditing(false); setDraft(null); };
  const handleSave = async () => {
    setBusy(true);
    try {
      await api.put(`/locations/${id}`, {
        location_code: draft.location_code,
        location_name: draft.location_name,
        description:   draft.description,
        parent_id:     draft.parent_id || null,
      });
      setEditing(false);
      setDraft(null);
      showToast('Location updated');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!loc) return (
    <div className="page-container">
      <div className="loading-spinner"><div className="spinner" /><span>Loading…</span></div>
    </div>
  );

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => nav('/location-master')}>
            <MdArrowBack /> Back
          </button>
          <div>
            <div className="page-title"><MdLocationOn /> {loc.location_name}</div>
            <div className="page-subtitle">
              <code>{loc.location_code}</code>
              {loc.ancestors.length > 0 && (
                <span style={{ marginLeft: 8 }}>
                  · {loc.ancestors.map(a => a.location_name).join(' › ')} › <strong>{loc.location_name}</strong>
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={busy}><MdRefresh /></button>
          {!editing ? (
            <button className="btn btn-primary" onClick={handleEdit}><MdEdit /> Edit</button>
          ) : (
            <>
              <button className="btn btn-outline" onClick={handleCancel} disabled={busy}>Cancel</button>
              <button className="btn btn-success" onClick={handleSave} disabled={busy}><MdSave /> Save</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        {/* LEFT: Details + Children */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title"><MdInfo style={{ color: 'var(--accent)' }} /> Properties</div></div>
            <div style={{ padding: 16 }}>
              {editing ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Code</label>
                    <input className="form-control" value={draft.location_code}
                      onChange={e => setDraft(d => ({ ...d, location_code: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={draft.location_name}
                      onChange={e => setDraft(d => ({ ...d, location_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Parent</label>
                    <SearchableSelect className="form-control" value={draft.parent_id || ''}
                      onChange={e => setDraft(d => ({ ...d, parent_id: e.target.value || null }))}>
                      <option value="">— Top level —</option>
                      {allLocations.filter(l => l.id !== loc.id).map(l =>
                        <option key={l.id} value={l.id}>{l.location_code} · {l.location_name}</option>)}
                    </SearchableSelect>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" rows={2} value={draft.description || ''}
                      onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
                  </div>
                </>
              ) : (
                <>
                  <KV label="Code"        value={<code>{loc.location_code}</code>} />
                  <KV label="Parent"      value={loc.parent_name ? <button className="btn btn-outline btn-sm" onClick={() => nav(`/locations/${loc.parent_id}`)}>{loc.parent_name}</button> : '—'} />
                  <KV label="Description" value={loc.description || '—'} />
                  <KV label="Created"     value={loc.created_at && new Date(loc.created_at).toLocaleString()} />
                  <KV label="Direct assets"   value={<span className="badge badge-info">{loc.asset_count}</span>} />
                  <KV label="Sub-locations"   value={<span className="badge badge-secondary">{loc.child_count}</span>} />
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title"><MdAccountTree style={{ color: 'var(--accent)' }} /> Sub-locations</div>
              <span className="badge badge-secondary">{loc.children.length}</span>
            </div>
            <div style={{ padding: 8 }}>
              {loc.children.length === 0 ? (
                <div style={{ padding: 16, color: '#888', fontSize: 13, textAlign: 'center' }}>None</div>
              ) : loc.children.map(c => (
                <div key={c.id}
                  onClick={() => nav(`/locations/${c.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    borderRadius: 6, cursor: 'pointer',
                    borderLeft: '3px solid var(--accent)',
                    marginBottom: 4, background: '#f8f9fb',
                  }}
                >
                  <span style={{ background: 'var(--primary)', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{c.location_code}</span>
                  <span style={{ fontWeight: 500 }}>{c.location_name}</span>
                  <span className="badge badge-info" style={{ marginLeft: 'auto' }}>{c.asset_count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Assets here + Readers */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title"><MdInventory style={{ color: 'var(--accent)' }} /> Assets at this location</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeDescendants}
                    onChange={e => setIncludeDescendants(e.target.checked)} />
                  Include sub-locations
                </label>
                <span className="badge badge-info">{assets.length}</span>
              </div>
            </div>
            {assets.length === 0 ? (
              <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
                No assets here{includeDescendants ? ' or in any sub-location' : ''}.
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>Asset</th><th>Tag</th><th>RFID</th><th>Status</th>{includeDescendants && <th>Location</th>}</tr>
                  </thead>
                  <tbody>
                    {assets.map(a => (
                      <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/assets/${a.id}`)}>
                        <td style={{ fontWeight: 500, color: 'var(--accent)' }}>{a.asset_name}</td>
                        <td><code style={{ fontSize: 11 }}>{a.asset_tag || '—'}</code></td>
                        <td><code style={{ fontSize: 11, color: '#1565c0' }}>{a.rfid_tag}</code></td>
                        <td><span className={`badge status-${a.status}`}>{a.status}</span></td>
                        {includeDescendants && <td style={{ fontSize: 12 }}>{a.location_name}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title"><MdRouter style={{ color: 'var(--accent)' }} /> Readers mapped here</div>
              <button className="btn btn-outline btn-sm" onClick={() => nav('/reader-manager')}>
                <MdSwapHoriz /> Manage in Reader Manager
              </button>
            </div>
            {readers.length === 0 ? (
              <div style={{ padding: 24, color: '#888', textAlign: 'center', fontSize: 13 }}>
                No readers assigned. Open Reader Manager and set this as the location for one or more readers.
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Name</th><th>Type</th><th>Address</th><th>Model</th><th>Status</th><th>Last Seen</th></tr></thead>
                  <tbody>
                    {readers.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 500 }}>{r.reader_name}</td>
                        <td>{r.reader_type}</td>
                        <td><code style={{ fontSize: 11 }}>{r.ip_address}{r.port ? `:${r.port}` : ''}</code></td>
                        <td>{r.model || '—'}</td>
                        <td><span className={`badge status-${r.status}`}>{r.status}</span></td>
                        <td style={{ fontSize: 11, color: '#666' }}>{r.last_seen ? new Date(r.last_seen).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const KV = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f2f5' }}>
    <span style={{ fontSize: 12, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    <span style={{ fontSize: 14 }}>{value}</span>
  </div>
);

export default LocationDetail;
